import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./AddRouteAssign.css";

// components
import PageHeader from "../../../components/common/PageHeader/PageHeader";
import CloseButton2 from "../../../components/common/CloseButton/CloseButton2";
import SelectBoxLarge from "../../../components/common/SelectBox/SelectBoxLarge";
import { ActiveUsers } from "../../../services/SalesApi";
import InputBoxLarge from "../../../components/common/InputBox/InputBoxLarge";
import {
  RouteAssignAdd,
  getActiveRoutes,
  RouteDetail,
} from "../../../services/routeApi";
import { isRequired, validateStartDate } from "../../../validation";
import AddButton from "../../../components/common/AddButton/AddButton";
import ErrorNotification from "../../../components/common/ErrorAlert/ErrorNotification";
import { showAlert } from "../../../components/common/ErrorAlert/ErrorAlert";
import { useTranslation } from "react-i18next";
import { SuccessAlert } from "../../../components/common/ErrorAlert/SuccessAlert";

const AddRouteAssign = () => {
  const { t } = useTranslation();
  const [salesPersons, setSalesPersons] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [routesAssign, setRouteAssign] = useState([]);
  const [routeInterval, setRouteInterval] = useState();
  const [routeType, setRouteType] = useState();
  const navigate = useNavigate();

  useEffect(() => {
    fetchSalesPersons();
    fetchRoutes();
  }, []);

  async function fetchSalesPersons() {
    const response = await ActiveUsers();
    const Options = response.data.data.map((user) => ({
      label: user.full_name,
      value: user.id,
    }));
    setSalesPersons(Options);
  }

  async function fetchRoutes() {
    const response = await getActiveRoutes();
    const Options = response.data.data.map((routes) => ({
      label: routes.name,
      value: routes.id,
    }));
    setRoutes(Options);
  }

  const fetchRouteDetail = async (id) => {
    try {
      const response = await RouteDetail(id);
      if (response.data.success === true) {
        const respData = response.data.data;
        console.log(respData);
        setRouteInterval(respData.route_intervals);
        setRouteType(respData.route_type);
      }
    } catch (e) {
      if (e?.response?.data?.error_code === 1001) {
        showAlert(e?.response?.data?.message)
      }
    }
  };

  const handleInputChange = (field, value) => {
    setRouteAssign((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateData = (data) => {
    const validationErrors = {};
    const validations = {
      route_id: isRequired,
      sales_person_id: isRequired,
      start_date: isRequired,
      end_date: isRequired,
    };

    for (const [field, validator] of Object.entries(validations)) {
      const validationResult = validator(data[field]);

      if (validationResult !== true) {
        validationErrors[field] = validationResult;
      }
    }

    return {
      isValid: Object.keys(validationErrors).length === 0,
      errors: validationErrors,
    };
  };

  const handleAdd = async () => {
    const { isValid, errors } = validateData(routesAssign);
    if (!isValid) {
      setFieldErrors(errors);
      return;
    }
    try {
      await RouteAssignAdd(routesAssign);
      SuccessAlert("Route assignment created successfully");
      handleClose();
    } catch (e) {
      if (e?.response?.data?.error_code === 1001) {
        showAlert(e?.response?.data?.message)
      }
      const errorMsg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        "Failed to create route assignment";
      showAlert(errorMsg);
    }
  };

  const handleClose = () => {
    navigate("/routes/assign");
  };

  const capitalize = (text) =>
    text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

  return (
    <div className="whole-container">
      <div className="path-header">
        <PageHeader
          breadcrumbs={[
            { label: "Route Management" },
            { label: "Route Assignment", path: "/routes/assign" },
            { label: "Create" },
          ]}
        />
      </div>
      <div className="page-actions-bar">
        <div className="page-name-add-uom common-text ftw-5">
          {t("New Route Assignment")}
        </div>
        <div className="page-name-rightside-icons">
          <AddButton onClick={() => handleAdd()} />
          <CloseButton2 onClick={() => handleClose()} />
        </div>
      </div>
      <div style={{ marginTop: "1rem" }}>
        <ErrorNotification
          errors={fieldErrors}
          width="50%"
          marginBottom={"1rem"}
        />
        <div className="add-fields-section route-assignment-container">
          <div className="add-fields-50-card">
            <SelectBoxLarge
              label="Select Route"
              important={true}
              options={routes}
              onChange={(e) => {
                handleInputChange("route_id", e.target.value);
                fetchRouteDetail(e.target.value);
              }}
              error={fieldErrors.route_id}
              validators={isRequired}
            />
            <SelectBoxLarge
              label="Sales Person"
              important={true}
              options={salesPersons}
              onChange={(e) =>
                handleInputChange("sales_person_id", e.target.value)
              }
              error={fieldErrors.sales_person_id}
              validators={isRequired}
            />
            <InputBoxLarge
              label="Start Date"
              type="date"
              important={true}
              labelClassName="medium-label"
              onChange={(e) => handleInputChange("start_date", e.target.value)}
              error={fieldErrors.start_date}
              validators={isRequired}
            />
            <InputBoxLarge
              label="End Date"
              type="date"
              important={true}
              labelClassName="medium-label"
              startDate={routesAssign?.start_date}
              onChange={(e) => handleInputChange("end_date", e.target.value)}
              error={fieldErrors.end_date}
              validators={[isRequired, validateStartDate]}
            />
          </div>
          <div className="route-preview-box">
            <div className="route-preview-header">{t("Route Preview")}</div>
            <div className="route-preview-body">
              <table>
                <thead>
                  <tr>
                    {routeType === "weekly" && <th>{t("Day")}</th>}
                    <th>{t("Customers")}</th>
                  </tr>
                </thead>
                <tbody>
                  {routeInterval ? (
                    routeInterval.map((interval, index) => (
                      <tr>
                        {routeType === "weekly" && (
                          <td>{capitalize(interval.interval)}</td>
                        )}
                        <td>
                          <div className="customer-card-container">
                            {interval.stop_data.map((s, index) => (
                              <div key={s.id} className="cust-card">
                                {s.label}
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <div className="empty-body">{t("No route selected")}</div>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddRouteAssign;
